'use client';

import { useEffect, useRef, useState } from 'react';
import { isEmbedded, useEcosystem } from './ecosystem';

/**
 * The embed bridge (brand/crow-extensions.html, « Apps intégrées »): an app shown inside a host
 * (CrowDrive or CrowBox in a Crowdex tab) draws neither its top bar nor its own column. It tells
 * the host what to draw instead, its sections, its page's title and primary action, with one
 * message, and the host tells it where to go.
 *
 *   app → host   { type: 'crow:nav', title, sections: [{ id, label, icon?, count?, active? }], actions?, path? }
 *   host → app   { type: 'crow:navigate', section } | { type: 'crow:navigate', url }
 *                { type: 'crow:action', id, choice? }
 *
 * Transport: in a frame, `postMessage` to the parent, only to the host's origin, the deployment's
 * Crowdex address (`GET /api/ecosystem`); a message is taken only from the parent window and that
 * origin. Crowdex Desktop shows apps in a `<webview>` (no parent window): its guest preload gives
 * the page `window.crowEmbedHost`, the same messages over Electron's IPC.
 */

export const EMBED_NAV = 'crow:nav';
export const EMBED_NAVIGATE = 'crow:navigate';
export const EMBED_ACTION = 'crow:action';

/** A section of the app, drawn in the host's left column (or its picker, docked). */
export interface EmbedSection {
  id: string;
  label: string;
  /** A Lucide icon's name (`hard-drive`, `inbox`). */
  icon?: string;
  /** Unread or waiting items, on the right. */
  count?: number;
  active?: boolean;
  /** The heading it is listed under (`Équipes`, `Boîtes`); none for the first group. */
  group?: string;
}

/** An action of the host's bar; one `primary` at most is drawn as such. */
export interface EmbedAction {
  id: string;
  label: string;
  icon?: string;
  primary?: boolean;
  disabled?: boolean;
  /** Choices: the action opens a menu of them, and the host sends the choice's id. */
  menu?: { id: string; label: string }[];
}

/** What the app shows: the host's bar and column follow it. */
export interface EmbedNav {
  type: typeof EMBED_NAV;
  title: string;
  sections: EmbedSection[];
  actions?: EmbedAction[];
  /** Where the app is (path, query and hash, on its origin): ↗ opens it there. */
  path?: string;
}

/** What the host asks of the app. */
export type EmbedCommand =
  | { type: typeof EMBED_NAVIGATE; section: string }
  | { type: typeof EMBED_NAVIGATE; url: string }
  | { type: typeof EMBED_ACTION; id: string; choice?: string };

const MAX_SECTIONS = 200;
const MAX_ACTIONS = 8;
const MAX_TEXT = 200;

type Fields = Record<string, unknown>;
const isObject = (value: unknown): value is Fields =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value.slice(0, MAX_TEXT) : undefined;

/** A path on the page's own origin (`/files/1?x#y`): never another site (`//evil`, `https:`). */
export function isAppPath(value: unknown): value is string {
  return typeof value === 'string' && /^\/(?![/\\])/.test(value) && value.length <= 2048;
}

function section(value: unknown): EmbedSection | null {
  if (!isObject(value)) return null;
  const id = text(value['id']);
  const label = text(value['label']);
  if (id === undefined || label === undefined) return null;
  const icon = text(value['icon']);
  const count = value['count'];
  const group = text(value['group']);
  return {
    id,
    label,
    ...(icon === undefined ? {} : { icon }),
    ...(typeof count === 'number' && Number.isFinite(count) && count > 0
      ? { count: Math.floor(count) }
      : {}),
    ...(value['active'] === true ? { active: true } : {}),
    ...(group === undefined ? {} : { group }),
  };
}

function action(value: unknown): EmbedAction | null {
  if (!isObject(value)) return null;
  const id = text(value['id']);
  const label = text(value['label']);
  if (id === undefined || label === undefined) return null;
  const icon = text(value['icon']);
  const menu = Array.isArray(value['menu'])
    ? value['menu'].flatMap((choice: unknown) => {
        if (!isObject(choice)) return [];
        const choiceId = text(choice['id']);
        const choiceLabel = text(choice['label']);
        return choiceId === undefined || choiceLabel === undefined
          ? []
          : [{ id: choiceId, label: choiceLabel }];
      })
    : undefined;
  return {
    id,
    label,
    ...(icon === undefined ? {} : { icon }),
    ...(value['primary'] === true ? { primary: true } : {}),
    ...(value['disabled'] === true ? { disabled: true } : {}),
    ...(menu === undefined || menu.length === 0 ? {} : { menu }),
  };
}

/** A `crow:nav` message as the host takes it (lengths capped), or null when it is none. */
export function parseEmbedNav(data: unknown): EmbedNav | null {
  if (!isObject(data) || data['type'] !== EMBED_NAV || !Array.isArray(data['sections']))
    return null;
  const sections = data['sections']
    .slice(0, MAX_SECTIONS)
    .flatMap((entry: unknown) => section(entry) ?? []);
  const actions = Array.isArray(data['actions'])
    ? data['actions'].slice(0, MAX_ACTIONS).flatMap((entry: unknown) => action(entry) ?? [])
    : [];
  const path = data['path'];
  return {
    type: EMBED_NAV,
    title: text(data['title']) ?? '',
    sections,
    ...(actions.length === 0 ? {} : { actions }),
    ...(isAppPath(path) ? { path } : {}),
  };
}

/** A command of the host as the app takes it, or null when it is none. */
export function parseEmbedCommand(data: unknown): EmbedCommand | null {
  if (!isObject(data)) return null;
  if (data['type'] === EMBED_NAVIGATE) {
    const sectionId = text(data['section']);
    if (sectionId !== undefined) return { type: EMBED_NAVIGATE, section: sectionId };
    const url = data['url'];
    return isAppPath(url) ? { type: EMBED_NAVIGATE, url } : null;
  }
  if (data['type'] === EMBED_ACTION) {
    const id = text(data['id']);
    const choice = text(data['choice']);
    return id === undefined
      ? null
      : { type: EMBED_ACTION, id, ...(choice === undefined ? {} : { choice }) };
  }
  return null;
}

/** What Crowdex Desktop's guest preload gives an embedded page (`window.crowEmbedHost`). */
export interface EmbedHostChannel {
  post(message: EmbedNav): void;
  /** Calls `listener` with each message of the host; returns the unsubscribe. */
  subscribe(listener: (message: unknown) => void): () => void;
}

/** The way to the host: post the navigation, hear its commands. */
export interface EmbedTransport {
  post(message: EmbedNav): void;
  listen(listener: (command: EmbedCommand) => void): () => void;
}

/**
 * The origin of the window framing this page, when it is one of `hosts`: what the browser says
 * (`location.ancestorOrigins`), else the page that opened the frame (`document.referrer`).
 */
export function frameHostOrigin(win: Window, hosts: readonly string[]): string | null {
  const ancestors = win.location.ancestorOrigins as DOMStringList | undefined;
  const candidates = [
    ancestors !== undefined && ancestors.length > 0 ? ancestors[0] : undefined,
    (() => {
      try {
        return win.document.referrer ? new URL(win.document.referrer).origin : undefined;
      } catch {
        return undefined;
      }
    })(),
  ];
  return candidates.find((origin) => origin !== undefined && hosts.includes(origin)) ?? null;
}

/**
 * The transport to the host this page is shown in: Crowdex Desktop's channel, or the parent
 * window at one of `hosts` (the deployment's host origins). Null when neither (not embedded, or a
 * frame of a site that is no host: it is told nothing).
 */
export function embedTransport(win: Window, hosts: readonly string[]): EmbedTransport | null {
  const channel = (win as Window & { crowEmbedHost?: EmbedHostChannel }).crowEmbedHost;
  if (channel !== undefined)
    return {
      post: (message) => channel.post(message),
      listen: (listener) =>
        channel.subscribe((message) => {
          const command = parseEmbedCommand(message);
          if (command !== null) listener(command);
        }),
    };
  if (win.parent === win) return null;
  const origin = frameHostOrigin(win, hosts);
  if (origin === null) return null;
  return {
    post: (message) => win.parent.postMessage(message, origin),
    listen: (listener) => {
      const heard = (event: MessageEvent) => {
        if (event.source !== win.parent || event.origin !== origin) return;
        const command = parseEmbedCommand(event.data);
        if (command !== null) listener(command);
      };
      win.addEventListener('message', heard);
      return () => win.removeEventListener('message', heard);
    },
  };
}

/** The apps that show others embedded (their origins may frame the apps and hear their nav). */
export const EMBED_HOSTS = ['crowdex'] as const;

export interface EmbeddedNavigation {
  /** Shown inside a host: the app hides its top bar and its column. */
  embedded: boolean;
  /** The host draws the sections, title and actions sent (a transport was found). */
  connected: boolean;
}

/**
 * For an app's shell: when it is embedded, sends `nav` (its sections, its page's title and
 * actions, where it is) to the host each time it changes, and runs the host's commands:
 * `onSection` for a section picked in the host's column, `onNavigate` for a path, `onAction` for
 * a click on an action of the host's bar. Does nothing when the app is not embedded.
 */
export function useEmbeddedNavigation(
  nav: Omit<EmbedNav, 'type'> | null,
  handlers: {
    onSection(id: string): void;
    onNavigate(path: string): void;
    onAction?(id: string, choice?: string): void;
  },
): EmbeddedNavigation {
  const [embedded] = useState(() => isEmbedded());
  const addresses = useEcosystem();
  const [transport, setTransport] = useState<EmbedTransport | null>(null);
  const hosts = EMBED_HOSTS.flatMap((id) => addresses.get(id) ?? []).join(' ');
  useEffect(() => {
    if (!embedded) return;
    setTransport(embedTransport(window, hosts ? hosts.split(' ') : []));
  }, [embedded, hosts]);

  // The latest handlers, without sending again when only they change.
  const latest = useRef(handlers);
  latest.current = handlers;
  useEffect(() => {
    if (transport === null) return;
    return transport.listen((command) => {
      if (command.type === EMBED_ACTION) latest.current.onAction?.(command.id, command.choice);
      else if ('section' in command) latest.current.onSection(command.section);
      else latest.current.onNavigate(command.url);
    });
  }, [transport]);

  const message = nav === null ? null : JSON.stringify({ type: EMBED_NAV, ...nav });
  useEffect(() => {
    if (transport === null || message === null) return;
    transport.post(JSON.parse(message) as EmbedNav);
  }, [transport, message]);

  return { embedded, connected: transport !== null };
}
